// Developer: Nithin | Date: 2026-07-26 | Added dispatchNo and invoiceNumber fields to dispatch form
// Developer: Nithin | Date: 2026-07-26 | dispatchNo now editable input; invoiceNumber empty by default; orderQuotationNo fills from sale.invoiceNo
import { useState, useEffect, useCallback } from "react";
import api from "@/lib/API";
import toast from "react-hot-toast";

export const useSalesDispatchLogic = ({ sale, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [dispatchInfo, setDispatchInfo] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [truckNumber, setTruckNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [toContactPerson, setToContactPerson] = useState("");
  const [toPhone, setToPhone] = useState("");
  const [dispatchNo, setDispatchNo] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  // orderQuotationNo is read-only — auto-resolved from sale data
  const [orderQuotationNo, setOrderQuotationNo] = useState("");

  const dispatchStatus = dispatchInfo?.dispatchStatus || sale?.dispatchStatus || 'pending_store';
  const recordType = sale?.sourceModel || (sale?.docType === 'Invoice' ? 'sale'
    : sale?.docType === 'Final Quotation' ? 'final'
    : 'order');

  const fetchInfo = useCallback(async () => {
    if (!sale?._id) return;
    setLoading(true);
    try {
      const res = await api.get(`/inventory/sales/${sale._id}/dispatch`, {
        params: { type: recordType }
      });
      const info = res.data;
      setDispatchInfo(info);
      if (info.dispatchStatus === 'received_store' || info.dispatchStatus === 'dispatch_started' || info.dispatchStatus === 'delivered') {
        setDriverName(info.driverName || "");
        setTruckNumber(info.truckNumber || "");
        setNotes(info.dispatchNotes || "");

        // Auto-fill fromAddress from saved value or sale store address
        setFromAddress(info.fromAddress || sale?.storeAddress || sale?.store?.address || "");

        // Auto-fill toAddress/contact from saved value or sale delivery fields
        const resolveName = (v) => !v ? '' : typeof v === 'object' ? (v.name || v.title || '') : String(v);
        setToAddress(info.toAddress || [
          sale?.deliveryAddressLine1, sale?.deliveryAddressLine2,
          sale?.deliveryCity, sale?.deliveryState,
        ].filter(Boolean).join(', ') || sale?.deliveryAddress || sale?.shippingAddress || "");
        setToContactPerson(info.toContactPerson || sale?.deliveryContactPerson || resolveName(sale?.customer) || sale?.customerName || "");
        setToPhone(info.toPhone || sale?.deliveryPhone || sale?.customerPhone || sale?.phone || "");

        // Dispatch No: restore saved value only (user enters manually)
        setDispatchNo(info.dispatchNo || "");

        // Invoice Number: restore saved value only — user enters it manually, do NOT pre-fill from sale.invoiceNo
        setInvoiceNumber(info.invoiceNumber || "");

        // Order/Quotation No: auto-resolved — sale.invoiceNo IS the order/quote reference in this system
        setOrderQuotationNo(
          info.orderQuotationNo ||
          sale?.invoiceNo ||
          sale?.orderNo || sale?.quotationNo || sale?.orderNumber ||
          sale?.quoteNo || sale?.quoteNumber || sale?.saleOrderNo || ""
        );
      }
    } catch (err) {
      console.error("Failed to fetch dispatch info:", err);
    } finally {
      setLoading(false);
    }
  }, [sale?._id, recordType]);

  useEffect(() => {
    if (sale?._id) fetchInfo();
  }, [sale?._id, fetchInfo]);

  const handleMarkAsReceived = async () => {
    setLoading(true);
    try {
      await api.patch(`/inventory/sales/${sale._id}/dispatch/receive`, null, {
        params: { type: recordType }
      });
      toast.success("Marked as received in store");
      await fetchInfo();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to mark as received");
    } finally {
      setLoading(false);
    }
  };

  const handleStartDispatch = async () => {
    if (!driverName.trim()) {
      toast.error("Driver Name is required");
      return;
    }
    if (!truckNumber.trim()) {
      toast.error("Truck Number is required");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/inventory/sales/${sale._id}/dispatch/start`,
        {
          driverName: driverName.trim(),
          truckNumber: truckNumber.trim(),
          notes,
          fromAddress: fromAddress.trim(),
          toAddress: toAddress.trim(),
          toContactPerson: toContactPerson.trim(),
          toPhone: toPhone.trim(),
          dispatchNo: dispatchNo.trim(),
          invoiceNumber: invoiceNumber.trim(),
          orderQuotationNo: orderQuotationNo.trim(),
        },
        { params: { type: recordType } }
      );
      toast.success("Dispatch started");
      await fetchInfo();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start dispatch");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    setLoading(true);
    try {
      await api.patch(`/inventory/sales/${sale._id}/dispatch/deliver`, null, {
        params: { type: recordType }
      });
      toast.success("Delivery confirmed");
      await fetchInfo();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to confirm delivery");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (dispatchStatus !== 'delivered') { toast.error('A receipt is available after delivery is confirmed'); return; }
    const info = dispatchInfo || sale || {};

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(v) || 0);

    const items = info.items || sale?.items || [];
    const itemRows = items.map((it, idx) => `
      <tr>
        <td style="text-align:center;padding:6px 8px;font-size:11px;border:0.5pt solid #d1d5db;">${idx + 1}</td>
        <td style="padding:6px 8px;font-size:11px;border:0.5pt solid #d1d5db;font-weight:600;">${it.itemName || it.name || 'Item'}</td>
        <td style="text-align:center;padding:6px 8px;font-size:11px;border:0.5pt solid #d1d5db;">${it.qty || it.quantity || 0}</td>
        <td style="text-align:right;padding:6px 8px;font-size:11px;border:0.5pt solid #d1d5db;">${fmtINR(it.rate || 0)}</td>
        <td style="text-align:right;padding:6px 8px;font-size:11px;border:0.5pt solid #d1d5db;font-weight:700;">${fmtINR(it.amount || 0)}</td>
      </tr>
    `).join('');

    const totalAmount = items.reduce((sum, it) => sum + Number(it.amount || 0), 0);

    // Developer: Nithin | Date: 2026-07-26 | Receipt reads saved dispatch fields; addresses match form (Billed To / Shipped To)
    const billedToAddr = info.fromAddress || sale?.storeAddress || sale?.store?.address || '—';
    const shippedToAddr = info.toAddress || [sale?.deliveryAddressLine1, sale?.deliveryAddressLine2, sale?.deliveryCity, sale?.deliveryState].filter(Boolean).join(', ') || sale?.deliveryAddress || sale?.shippingAddress || '—';

    const receiptDispatchNo  = info.dispatchNo  || dispatchNo || '—';
    const receiptInvoiceNo   = info.invoiceNumber || invoiceNumber || '—';
    const receiptOrderQuotNo = info.orderQuotationNo || orderQuotationNo || sale?.invoiceNo || '—';

    const docHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>Delivery Receipt - ${sale?.invoiceNo || sale?._id || ''}</title>
  <style>
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } .no-print { display:none !important; } }
    body { margin:0; padding:20px; font-family:Arial,Helvetica,sans-serif; color:#1e293b; background:#e2e8f0; }
    .receipt { width:700px; margin:0 auto; background:#fff; border:0.5pt solid #9ca3af; }
    .header { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; border-bottom:0.5pt solid #9ca3af; }
    .header-left { display:flex; align-items:center; gap:12px; }
    .header-left img { height:48px; object-fit:contain; }
    .brand-name { font-size:20px; font-weight:800; color:#1e293b; letter-spacing:-0.3px; }
    .brand-sub { font-size:9px; color:#64748b; margin-top:2px; }
    .store-info { font-size:10px; color:#475569; line-height:1.6; text-align:right; }
    .title-bar { text-align:center; padding:6px 0; font-size:13px; font-weight:800; letter-spacing:2px; border-bottom:0.5pt solid #9ca3af; background:#f8fafc; }
    .info-row { display:flex; border-bottom:0.5pt solid #9ca3af; }
    .info-col { flex:1; padding:10px 16px; }
    .info-col+.info-col { border-left:0.5pt solid #9ca3af; }
    .info-label { font-size:9px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; }
    .info-value { font-size:11px; font-weight:600; }
    table { width:100%; border-collapse:collapse; }
    th { background:#f1f5f9; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; padding:6px 8px; border:0.5pt solid #9ca3af; }
    td { padding:6px 8px; font-size:11px; border:0.5pt solid #d1d5db; }
    .addr-label { font-size:10px; font-weight:700; font-style:italic; color:#1e293b; margin-bottom:4px; }
    .addr-text { font-size:10px; color:#334155; line-height:1.5; white-space:pre-line; }
    .total-row td { background:#ecfdf5; font-weight:800; font-size:12px; }
    .notes { padding:10px 16px; border-top:0.5pt solid #9ca3af; font-size:9px; color:#475569; line-height:1.6; }
    .footer { padding:10px 16px; border-top:0.5pt solid #9ca3af; text-align:center; color:#94a3b8; font-size:8px; }
    .print-btn { text-align:center; margin-top:20px; }
    .print-btn button { padding:10px 24px; font-size:14px; background:#ea580c; color:#fff; border:none; border-radius:6px; cursor:pointer; }
    .print-btn button:hover { background:#c2410c; }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <div class="header-left">
        <img src="/verve-kitchens-logo-01.png" alt="Verve Kitchens" onerror="this.style.display='none'" />
        <div>
          <div class="brand-name">Verve Kitchens</div>
          <div class="brand-sub">Delivery Receipt</div>
        </div>
      </div>
      <div class="store-info">
        ${sale?.storeName ? `<div style="font-weight:700;">${sale.storeName}</div>` : ''}
        ${sale?.storeAddress ? `<div>${sale.storeAddress}</div>` : ''}
        ${sale?.storePhone ? `<div>${sale.storePhone}</div>` : ''}
      </div>
    </div>

    <div class="title-bar">DELIVERY RECEIPT</div>

    <div class="info-row">
      <div class="info-col">
        <table>
          <tr><td class="info-label" style="width:120px;">Dispatch No.</td><td class="info-value">: ${receiptDispatchNo}</td></tr>
          <tr><td class="info-label">Invoice No.</td><td class="info-value">: ${receiptInvoiceNo}</td></tr>
          <tr><td class="info-label">Order / Quote No.</td><td class="info-value">: ${receiptOrderQuotNo}</td></tr>
          <tr><td class="info-label">Date</td><td class="info-value">: ${fmtDate(info.deliveryConfirmedAt || new Date())}</td></tr>
        </table>
      </div>
      <div class="info-col">
        <table>
          <tr><td class="info-label" style="width:90px;">Driver</td><td class="info-value">: ${info.driverName || '—'}</td></tr>
          <tr><td class="info-label">Truck No.</td><td class="info-value">: ${info.truckNumber || '—'}</td></tr>
          <tr><td class="info-label">Dispatched</td><td class="info-value">: ${fmtDateTime(info.dispatchStartedAt)}</td></tr>
          <tr><td class="info-label">Delivered</td><td class="info-value">: ${fmtDateTime(info.deliveryConfirmedAt)}</td></tr>
        </table>
      </div>
    </div>

    <div class="info-row">
      <div class="info-col">
        <div class="addr-label">Billed To</div>
        <div class="addr-text">${billedToAddr.replace(/\\n/g, '<br/>')}</div>
      </div>
      <div class="info-col">
        <div class="addr-label">Shipped To</div>
        <div class="addr-text">${shippedToAddr.replace(/\\n/g, '<br/>')}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:40px;text-align:center;">#</th>
          <th style="text-align:left;">Item Description</th>
          <th style="width:60px;text-align:center;">Qty</th>
          <th style="width:90px;text-align:right;">Rate</th>
          <th style="width:100px;text-align:right;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows || '<tr><td colspan="5" style="text-align:center;padding:16px;color:#94a3b8;">No items</td></tr>'}
        <tr class="total-row">
          <td colspan="4" style="text-align:right;padding:8px 16px;">Total</td>
          <td style="text-align:right;padding:8px 16px;">${fmtINR(totalAmount)}</td>
        </tr>
      </tbody>
    </table>

    ${info.dispatchNotes ? `<div class="notes"><strong>Notes:</strong> ${info.dispatchNotes}</div>` : ''}

    <div class="footer">This receipt is system generated.</div>
  </div>
  <div class="print-btn no-print">
    <button onclick="window.print()">Save as PDF</button>
  </div>
</body>
</html>`;

    const blob = new Blob([docHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const filename = `DeliveryReceipt-${sale?.invoiceNo || sale?._id || 'download'}.html`;
    const w = window.open(url, '_blank');
    if (w) {
      w.document.title = filename;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Receipt opened in new tab');
    } else {
      toast.error('Popup blocked. Please allow popups for this site.');
      URL.revokeObjectURL(url);
    }
  };
  const resetForm = () => {
    setDriverName("");
    setTruckNumber("");
    setNotes("");
    setFromAddress("");
    setToAddress("");
    setToContactPerson("");
    setToPhone("");
    setDispatchNo("");
    setInvoiceNumber("");
    setOrderQuotationNo("");
  };

  return {
    loading,
    dispatchInfo,
    dispatchStatus,
    driverName, setDriverName,
    truckNumber, setTruckNumber,
    notes, setNotes,
    fromAddress, setFromAddress,
    toAddress, setToAddress,
    toContactPerson, setToContactPerson,
    toPhone, setToPhone,
    dispatchNo, setDispatchNo,      // editable — user types their dispatch number
    invoiceNumber, setInvoiceNumber,
    orderQuotationNo,               // read-only auto-filled from sale reference
    handleMarkAsReceived,
    handleStartDispatch,
    handleConfirmDelivery,
    handleDownloadReceipt,
    resetForm,
    fetchInfo,
  };
};
